import React from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPanel,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Input,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import type { EntityViewRow } from "#app/lib/data";
import { useUpdateEntityView } from "#app/lib/hooks/use-entity-views";
import { AnalyticsContext, DashboardEvent, trackEvent } from "#app/lib/analytics";

type RenameDashboardDialogProps = Omittable<{
  /** The dashboard's view row. Null keeps the dialog closed. */
  view: Pick<EntityViewRow, "id" | "name"> | null;
  onOpenChange: (open: boolean) => void;
}>;

/**
 * Rename prompt for a dashboard — the row menu's Rename on surfaces whose row
 * cannot swap to an inline input (the directory table, the module menu's
 * rows). A dashboard's own tab swaps in place instead (`TabsTabInput`). The
 * workflow rename dialog's grammar: one AlertDialog at every breakpoint.
 */
export function RenameDashboardDialog({
  view,
  onOpenChange,
}: RenameDashboardDialogProps) {
  const updateView = useUpdateEntityView();
  const [draft, setDraft] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // Seed the draft from the target as the dialog opens; keyed on identity so
  // re-opening on another row never shows the previous row's draft.
  const viewId = view?.id ?? null;
  React.useEffect(() => {
    setDraft(view?.name ?? "");
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the name seeds once per target; live cache renames must not clobber an open draft.
  }, [viewId]);

  const commit = async () => {
    if (!viewId) return;
    const name = draft.trim();
    if (!name || name === view?.name) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      // A refused write is already toasted by the hook; the dialog stays
      // open with the draft standing, so the retry is one press.
      const updated = await updateView(viewId, { name });
      if (!updated) return;
      trackEvent({
        context: AnalyticsContext.Dashboard,
        event: DashboardEvent.Rename,
        data: { via: "row_menu" },
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AlertDialog open={view != null} onOpenChange={onOpenChange}>
      <AlertDialogPopup
        render={
          <Box
            as="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (draft.trim()) void commit();
            }}
          />
        }
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Rename dashboard</AlertDialogTitle>
          <AlertDialogDescription>
            Enter a new name for this dashboard.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogPanel>
          <Input
            aria-label="Dashboard name"
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            value={draft}
          />
        </AlertDialogPanel>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            Cancel
          </AlertDialogClose>
          <Button
            disabled={saving || !draft.trim()}
            loading={saving}
            type="submit"
          >
            Rename
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
